import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, Sparkles, Building2, Rocket, ShieldCheck, Mail } from 'lucide-react';
import Magnetic from '../innovher/Magnetic';
import SEO from '../SEO';
import { portalApi } from '../../lib/portalApi';
import { QuestionField, DocumentField } from '../accelerator/shared/DynamicFormFields';

function Field({ label, type = 'text', value, onChange, placeholder }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs uppercase tracking-wider text-white/60 font-medium">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/[0.03] border border-white/10 focus:border-[#d42b92] focus:bg-white/[0.05] rounded-xl px-4 py-3.5 text-white text-sm outline-none transition-all placeholder:text-white/25 focus:ring-1 focus:ring-[#d42b92]"
      />
    </div>
  );
}

export default function ApplyPage() {
  const [formBank, setFormBank] = useState(null);
  const [identity, setIdentity] = useState({
    fullName: '',
    businessEmail: '',
    phoneE164: '',
    companyName: '',
  });
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [qFiles, setQFiles] = useState({});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    portalApi
      .getApplyForm()
      .then(setFormBank)
      .catch(() =>
        setFormBank({
          stages: [],
          questions: [],
          documentRequirements: [],
        })
      );
  }, []);

  const allQuestions = useMemo(() => {
    return formBank?.questions || [];
  }, [formBank]);

  const allDocs = useMemo(() => {
    return formBank?.documentRequirements || [];
  }, [formBank]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (
      identity.fullName.trim().length < 2 ||
      !identity.businessEmail.trim() ||
      identity.phoneE164.trim().length < 8 ||
      identity.companyName.trim().length < 2
    ) {
      return setError('Please complete name, email, phone, and company name.');
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('fullName', identity.fullName);
      fd.append('businessEmail', identity.businessEmail);
      fd.append('phoneE164', identity.phoneE164);
      fd.append('companyName', identity.companyName);
      fd.append('answers', JSON.stringify(answers));
      Object.entries(files).forEach(([reqId, file]) => {
        if (file) fd.append(`doc_${reqId}`, file);
      });
      Object.entries(qFiles).forEach(([qid, file]) => {
        if (file) fd.append(`qfile_${qid}`, file);
      });
      await portalApi.submitApplication(fd);
      setSent(true);
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="relative min-h-screen bg-[#08080a] text-white flex items-center justify-center p-6 sm:p-8 overflow-hidden">
        <SEO title="Application Received" description="Your InnovACE application has been received. InnovHer will review and respond within seven business days." />
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-[#d42b92]/10 blur-[180px] pointer-events-none" />
        <div className="relative max-w-lg w-full text-center rounded-3xl border border-white/10 bg-[#0d0d12]/90 backdrop-blur-xl p-8 sm:p-12 shadow-2xl">
          <div className="mx-auto w-16 h-16 rounded-full bg-[#d42b92]/20 border border-[#d42b92]/50 flex items-center justify-center mb-6 shadow-[0_0_24px_rgba(212,43,146,0.35)]">
            <Check className="w-8 h-8 text-[#ff8ec5]" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">Application received</h1>
          <p className="text-white/60 mt-4 text-sm sm:text-base leading-relaxed">
            Your InnovAce application is in review. If accepted, you and your startup will be invited
            into onboarding, no account is created until then.
          </p>
          <div className="mt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 hover:bg-white/15 text-white text-xs font-semibold tracking-wider transition-colors"
            >
              Return Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#08080a] text-white selection:bg-[#d42b92]/30 overflow-hidden">
      <SEO
        title="Apply to InnovACE"
        description="Apply to InnovACE, InnovHer's portfolio acceleration pathway for founders ready to move from potential to measurable growth."
        keywords="apply InnovACE, InnovHer accelerator application, startup accelerator India, Jaipur accelerator"
      />

      {/* Ambient background glows */}
      <div className="absolute top-20 right-1/4 w-[600px] h-[600px] rounded-full bg-[#d42b92]/10 blur-[180px] pointer-events-none" />
      <div className="absolute top-[600px] left-10 w-[500px] h-[500px] rounded-full bg-[#d42b92]/8 blur-[160px] pointer-events-none" />

      {/* Header section with proper top padding for fixed navbar */}
      <section className="relative pt-32 sm:pt-40 pb-12 sm:pb-16 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#d42b92]/30 bg-[#d42b92]/10 text-[#ff8ec5] text-xs font-semibold tracking-[0.25em] uppercase mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#d42b92] animate-pulse" />
              <span>InnovAce · Apply</span>
            </div> */}

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.08]">
              Join the InnovAce <span className="text-[#d42b92]">cohort</span>
            </h1>

            <p className="mt-4 text-white/70 text-sm sm:text-base md:text-lg leading-relaxed">
              Dynamic InnovAce application, questions come from the admin form builder. Submitting creates a request; after Accept, founder and startup move into onboarding.
            </p>
          </div>
        </div>
      </section>

      {/* Main Form & Information Section */}
      <section className="relative py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left Column: Single-Step Form Card */}
            <div className="lg:col-span-7">
              <div className="rounded-3xl border border-white/10 bg-[#0d0d12]/90 backdrop-blur-xl p-6 sm:p-10 shadow-2xl relative overflow-hidden">
                {/* Subtle card glow accent */}
                <div className="absolute top-0 right-0 w-44 h-44 bg-[#d42b92]/10 blur-3xl pointer-events-none" />

                <div className="flex items-center justify-between pb-6 mb-7 border-b border-white/10">
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight">
                      Application Details
                    </h2>
                    <p className="text-xs text-white/50 mt-1">
                      Complete your details to submit your application in one step
                    </p>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-[#d42b92]/15 border border-[#d42b92]/40 text-[#ff8ec5] text-[11px] font-semibold tracking-wide shrink-0">
                    1-Step Submission
                  </span>
                </div>

                <form onSubmit={submit} className="space-y-6">
                  {/* Core Identity Fields */}
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="Full name *"
                      value={identity.fullName}
                      onChange={(v) => setIdentity({ ...identity, fullName: v })}
                      placeholder="Your full name"
                    />
                    <Field
                      label="Business email *"
                      type="email"
                      value={identity.businessEmail}
                      onChange={(e) => setIdentity({ ...identity, businessEmail: e })}
                      placeholder="you@company.com"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="Phone *"
                      value={identity.phoneE164}
                      onChange={(v) => setIdentity({ ...identity, phoneE164: v })}
                      placeholder="10-digit or +91…"
                    />
                    <Field
                      label="Company name *"
                      value={identity.companyName}
                      onChange={(v) => setIdentity({ ...identity, companyName: v })}
                      placeholder="Your company or venture name"
                    />
                  </div>

                  {/* Dynamic Questions & Docs if present */}
                  {allQuestions.length > 0 && (
                    <div className="pt-6 border-t border-white/10 space-y-5">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-white/70">
                        Additional Information
                      </h3>
                      {allQuestions.map((q) => (
                        <QuestionField
                          key={q.id}
                          question={q}
                          value={answers[q.id]}
                          onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
                          onFile={(f) => setQFiles({ ...qFiles, [q.id]: f })}
                        />
                      ))}
                    </div>
                  )}

                  {allDocs.length > 0 && (
                    <div className="pt-6 border-t border-white/10 space-y-5">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-white/70">
                        Required Documents
                      </h3>
                      {allDocs.map((d) => (
                        <DocumentField
                          key={d.id}
                          doc={d}
                          onFile={(f) => setFiles({ ...files, [d.id]: f })}
                        />
                      ))}
                    </div>
                  )}

                  {error && (
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-xs text-white/40">
                      All submissions are reviewed by the InnovAce investment team.
                    </p>
                    <Magnetic>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#d42b92] hover:bg-[#ff8ec5] text-white px-8 py-3.5 rounded-full font-semibold text-sm shadow-[0_0_24px_rgba(212,43,146,0.35)] hover:shadow-[0_0_32px_rgba(212,43,146,0.55)] transition-all duration-300 disabled:opacity-50"
                      >
                        {loading ? 'Submitting…' : 'Submit application'}
                        <ArrowUpRight className="w-4 h-4" />
                      </button>
                    </Magnetic>
                  </div>
                </form>
              </div>
            </div>

            {/* Right Column: Pathway Notice & InnovAce Context */}
            <div className="lg:col-span-5 space-y-6">
              {/* Alternate Pathways Card */}
              <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-7 backdrop-blur-md relative overflow-hidden">
                <div className="text-[#ff8ec5] text-xs font-semibold tracking-[0.25em] uppercase mb-3 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-[#ff8ec5]" />
                  <span>Other Pathways</span>
                </div>
                <p className="text-sm text-white/70 leading-relaxed">
                  Looking for venture studio or other InnovHer pathways?{' '}
                </p>
                <div className="mt-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#d42b92]/40 bg-[#d42b92]/10 hover:bg-[#d42b92]/20 text-[#ff8ec5] hover:text-white text-xs font-semibold transition-all"
                  >
                    Contact Us →
                  </Link>
                </div>
              </div>

              {/* Accelerator Highlights */}
              <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-7 backdrop-blur-md space-y-5">
                <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">
                  What Happens Next
                </div>

                <div className="space-y-4 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#d42b92]/20 border border-[#d42b92]/50 text-[#ff8ec5] text-xs font-bold grid place-items-center shrink-0 mt-0.5">
                      1
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Application Review</h4>
                      <p className="text-xs text-white/55 mt-1 leading-relaxed">
                        Submitting creates a request reviewed by our evaluation committee.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-white/10 border border-white/15 text-white/70 text-xs font-bold grid place-items-center shrink-0 mt-0.5">
                      2
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Founder Interaction</h4>
                      <p className="text-xs text-white/55 mt-1 leading-relaxed">
                        Shortlisted founders are invited for an in-depth conversation.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-white/10 border border-white/15 text-white/70 text-xs font-bold grid place-items-center shrink-0 mt-0.5">
                      3
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Cohort Onboarding</h4>
                      <p className="text-xs text-white/55 mt-1 leading-relaxed">
                        After Accept, founder and startup move directly into onboarding.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-5 border-t border-white/10 flex items-center justify-between text-xs text-white/40">
                  <span>InnovHer Accelerator</span>
                  <span>Jaipur · India</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
